import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  useTheme
} from '@mui/material';
import {
  Event as EventIcon,
  LocalOffer as OfferIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Login as LoginIcon,
  PersonAdd as SignUpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BRAND_NAME } from '../config';

const Landing = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const features = [
    {
      icon: <EventIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Event Management',
      description: 'Create and manage events with sophisticated tools and analytics'
    },
    {
      icon: <OfferIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Offer System',
      description: 'Build compelling offers with templates and brand integration'
    },
    {
      icon: <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Brand Management',
      description: 'Organize and showcase your brand portfolio effectively'
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Analytics',
      description: 'Track performance with detailed insights and reporting'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Navigation */}
      <AppBar position="sticky" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, color: 'primary.main' }}>
            {BRAND_NAME}
          </Typography>
          <Button
            color="inherit"
            startIcon={<LoginIcon />}
            onClick={() => navigate('/signin')}
            sx={{ mr: 2 }}
          >
            Sign In
          </Button>
          <Button
            variant="contained"
            startIcon={<SignUpIcon />}
            onClick={() => navigate('/signup')}
          >
            Sign Up
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ py: 12, textAlign: 'center' }}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              mb: 3,
              background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Welcome to {BRAND_NAME}
          </Typography>
          
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}
          >
            A sophisticated platform for managing events, offers, and brands with modern design and powerful features
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<SignUpIcon />}
              onClick={() => navigate('/signup')}
              sx={{ px: 4, py: 1.5 }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<LoginIcon />}
              onClick={() => navigate('/signin')}
              sx={{ px: 4, py: 1.5 }}
            >
              Sign In
            </Button>
          </Box>
        </Box>

        {/* Features Section */}
        <Box sx={{ py: 8 }}>
          <Typography
            variant="h3"
            textAlign="center"
            sx={{ mb: 2, fontWeight: 600 }}
          >
            Powerful Features
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            Everything you need to manage your digital presence
          </Typography>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 2,
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Card sx={{ p: 6, background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)' }}>
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              Ready to get started?
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Join the users who trust {BRAND_NAME} for their business needs
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<SignUpIcon />}
              onClick={() => navigate('/signup')}
              sx={{ px: 6, py: 2 }}
            >
              Create Your Account
            </Button>
          </Card>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 4, borderTop: '1px solid', borderColor: 'divider', mt: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" textAlign="center">
            © 2025 {BRAND_NAME}. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Landing; 